import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { twitterAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface ProblemData {
  title: string;
  count: number;
  recommendation: string;
}

export interface AccountData {
  username: string;
  displayName: string;
  followers: number;
  following: number;
  tweets: number;
  profilePicture: string;
  bio: string;
  website: string;
  isVerified: boolean;
  location: string;
  joinDate: string;
}

export interface TweetData {
  id: number | string;
  content: string;
  likes: number;
  retweets: number;
  replies: number;
  reach: number;
  engagement: number;
  postedAt: string;
  hashtags: string[];
}

export interface TwitterState {
  account: AccountData | null;
  recentTweets: TweetData[];
  topTweets: TweetData[];
  engagement: ChartData | null;
  reach: ChartData | null;
  hashtags: ChartData | null;
  contentPerformance: ChartData | null;
  sentiment: ChartData | null;
  problems: ProblemData[];
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    account: boolean;
    recentTweets: boolean;
    topTweets: boolean;
    engagement: boolean;
    reach: boolean;
    hashtags: boolean;
    contentPerformance: boolean;
    sentiment: boolean;
    problems: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    account: string | null;
    recentTweets: string | null;
    topTweets: string | null;
    engagement: string | null;
    reach: string | null;
    hashtags: string | null;
    contentPerformance: string | null;
    sentiment: string | null;
    problems: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type TwitterAction =
  | { type: 'FETCH_ACCOUNT_START' }
  | { type: 'FETCH_ACCOUNT_SUCCESS'; payload: AccountData }
  | { type: 'FETCH_ACCOUNT_ERROR'; payload: string }
  | { type: 'FETCH_RECENT_TWEETS_START' }
  | { type: 'FETCH_RECENT_TWEETS_SUCCESS'; payload: TweetData[] }
  | { type: 'FETCH_RECENT_TWEETS_ERROR'; payload: string }
  | { type: 'FETCH_TOP_TWEETS_START' }
  | { type: 'FETCH_TOP_TWEETS_SUCCESS'; payload: TweetData[] }
  | { type: 'FETCH_TOP_TWEETS_ERROR'; payload: string }
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_REACH_START' }
  | { type: 'FETCH_REACH_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_REACH_ERROR'; payload: string }
  | { type: 'FETCH_HASHTAGS_START' }
  | { type: 'FETCH_HASHTAGS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_HASHTAGS_ERROR'; payload: string }
  | { type: 'FETCH_CONTENT_PERFORMANCE_START' }
  | { type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_CONTENT_PERFORMANCE_ERROR'; payload: string }
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_PROBLEMS_START' }
  | { type: 'FETCH_PROBLEMS_SUCCESS'; payload: ProblemData[] }
  | { type: 'FETCH_PROBLEMS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: TwitterState = {
  account: null,
  recentTweets: [],
  topTweets: [],
  engagement: null,
  reach: null,
  hashtags: null,
  contentPerformance: null,
  sentiment: null,
  problems: [],
  aiAnalysis: {},
  loading: {
    account: false,
    recentTweets: false,
    topTweets: false,
    engagement: false,
    reach: false,
    hashtags: false,
    contentPerformance: false,
    sentiment: false,
    problems: false,
    aiAnalysis: {},
  },
  error: {
    account: null,
    recentTweets: null,
    topTweets: null,
    engagement: null,
    reach: null,
    hashtags: null,
    contentPerformance: null,
    sentiment: null,
    problems: null,
    aiAnalysis: {},
  },
};

const twitterReducer = (state: TwitterState, action: TwitterAction): TwitterState => {
  switch (action.type) {
    case 'FETCH_ACCOUNT_START':
      return {
        ...state,
        loading: { ...state.loading, account: true },
        error: { ...state.error, account: null },
      };
    case 'FETCH_ACCOUNT_SUCCESS':
      return {
        ...state,
        account: action.payload,
        loading: { ...state.loading, account: false },
      };
    case 'FETCH_ACCOUNT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, account: false },
        error: { ...state.error, account: action.payload },
      };
    case 'FETCH_RECENT_TWEETS_START':
      return {
        ...state,
        loading: { ...state.loading, recentTweets: true },
        error: { ...state.error, recentTweets: null },
      };
    case 'FETCH_RECENT_TWEETS_SUCCESS':
      return {
        ...state,
        recentTweets: action.payload,
        loading: { ...state.loading, recentTweets: false },
      };
    case 'FETCH_RECENT_TWEETS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recentTweets: false },
        error: { ...state.error, recentTweets: action.payload },
      };
    case 'FETCH_TOP_TWEETS_START':
      return {
        ...state,
        loading: { ...state.loading, topTweets: true },
        error: { ...state.error, topTweets: null },
      };
    case 'FETCH_TOP_TWEETS_SUCCESS':
      return {
        ...state,
        topTweets: action.payload,
        loading: { ...state.loading, topTweets: false },
      };
    case 'FETCH_TOP_TWEETS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, topTweets: false },
        error: { ...state.error, topTweets: action.payload },
      };
    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };
    case 'FETCH_REACH_START':
      return {
        ...state,
        loading: { ...state.loading, reach: true },
        error: { ...state.error, reach: null },
      };
    case 'FETCH_REACH_SUCCESS':
      return {
        ...state,
        reach: action.payload,
        loading: { ...state.loading, reach: false },
      };
    case 'FETCH_REACH_ERROR':
      return {
        ...state,
        loading: { ...state.loading, reach: false },
        error: { ...state.error, reach: action.payload },
      };
    case 'FETCH_HASHTAGS_START':
      return {
        ...state,
        loading: { ...state.loading, hashtags: true },
        error: { ...state.error, hashtags: null },
      };
    case 'FETCH_HASHTAGS_SUCCESS':
      return {
        ...state,
        hashtags: action.payload,
        loading: { ...state.loading, hashtags: false },
      };
    case 'FETCH_HASHTAGS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, hashtags: false },
        error: { ...state.error, hashtags: action.payload },
      };
    case 'FETCH_CONTENT_PERFORMANCE_START':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: true },
        error: { ...state.error, contentPerformance: null },
      };
    case 'FETCH_CONTENT_PERFORMANCE_SUCCESS':
      return {
        ...state,
        contentPerformance: action.payload,
        loading: { ...state.loading, contentPerformance: false },
      };
    case 'FETCH_CONTENT_PERFORMANCE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: false },
        error: { ...state.error, contentPerformance: action.payload },
      };
    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };
    case 'FETCH_PROBLEMS_START':
      return {
        ...state,
        loading: { ...state.loading, problems: true },
        error: { ...state.error, problems: null },
      };
    case 'FETCH_PROBLEMS_SUCCESS':
      return {
        ...state,
        problems: action.payload,
        loading: { ...state.loading, problems: false },
      };
    case 'FETCH_PROBLEMS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, problems: false },
        error: { ...state.error, problems: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface TwitterContextType {
  state: TwitterState;
  fetchAccountData: () => Promise<void>;
  fetchRecentTweets: () => Promise<void>;
  fetchTopTweets: () => Promise<void>;
  fetchEngagementData: () => Promise<void>;
  fetchReachData: () => Promise<void>;
  fetchHashtagsData: () => Promise<void>;
  fetchContentPerformanceData: () => Promise<void>;
  fetchSentimentData: () => Promise<void>;
  fetchProblemsData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
}

const TwitterContext = createContext<TwitterContextType | undefined>(undefined);

export const TwitterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(twitterReducer, initialState);
  const { business } = useAuth();

  const fetchAccountData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ACCOUNT_START' });
    try {
      const response = await twitterAPI.getAccountData(business._id);
      const rawData = response.data.data;
      const details = rawData?.details || rawData;
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Map API response to AccountData interface
      const accountData: AccountData = {
        username: details?.username || '',
        displayName: details?.displayName || '',
        followers: details?.followerCount ?? 0,
        following: details?.followingCount ?? 0,
        tweets: details?.tweetCount ?? 0,
        profilePicture: toProxy(details?.profileImageUrl || ''),
        bio: details?.bio || '',
        website: details?.website || '',
        isVerified: details?.isVerified || false,
        location: details?.location || '',
        joinDate: details?.joinedDate || '',
      };
      
      dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: accountData });
    } catch (error) {
      dispatch({ type: 'FETCH_ACCOUNT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchRecentTweets = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_RECENT_TWEETS_START' });
    try {
      const response = await twitterAPI.getRecentTweets(business._id);
      const rawData = response.data.data;
      
      // Handle both single object and array responses
      const rawTweets = Array.isArray(rawData) 
        ? rawData 
        : (rawData && typeof rawData === 'object' ? [rawData] : []);
      
      // Map API response to TweetData interface
      const tweets: TweetData[] = rawTweets.map((tweet: any) => {
        // Calculate engagement percentage if not provided
        const totalEngagement = (tweet.likes ?? 0) + (tweet.retweetCount ?? 0) + (tweet.replyCount ?? 0);
        const reach = tweet.viewCount ?? tweet.reach ?? 0;
        const engagement = tweet.engagement ?? (reach > 0 ? Math.round((totalEngagement / reach) * 100 * 100) / 100 : 0);
        
        return {
          id: tweet.id || tweet._id || '',
          content: tweet.text || tweet.content || '',
          likes: tweet.likes ?? 0,
          retweets: tweet.retweetCount ?? 0,
          replies: tweet.replyCount ?? tweet.comments ?? 0,
          reach: reach,
          engagement: engagement,
          postedAt: tweet.createdAt || tweet.postedAt || '',
          hashtags: Array.isArray(tweet.hashtags) ? tweet.hashtags : [],
        };
      });
      
      dispatch({ type: 'FETCH_RECENT_TWEETS_SUCCESS', payload: tweets });
    } catch (error) {
      dispatch({ type: 'FETCH_RECENT_TWEETS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchTopTweets = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_TOP_TWEETS_START' });
    try {
      const response = await twitterAPI.getTopTweets(business._id);
      const rawTweets = Array.isArray(response.data.data) ? response.data.data : [];
      
      // Map API response to TweetData interface
      const tweets: TweetData[] = rawTweets.map((tweet: any) => {
        // Calculate engagement percentage if not provided
        const totalEngagement = (tweet.likes ?? 0) + (tweet.retweetCount ?? 0) + (tweet.replyCount ?? 0);
        const reach = tweet.viewCount ?? tweet.reach ?? 0;
        const engagement = tweet.engagement ?? (reach > 0 ? Math.round((totalEngagement / reach) * 100 * 100) / 100 : 0);
        
        return {
          id: tweet.id || tweet._id || '',
          content: tweet.text || tweet.content || '',
          likes: tweet.likes ?? 0,
          retweets: tweet.retweetCount ?? 0,
          replies: tweet.replyCount ?? tweet.comments ?? 0,
          reach: reach,
          engagement: engagement,
          postedAt: tweet.createdAt || tweet.postedAt || '',
          hashtags: Array.isArray(tweet.hashtags) ? tweet.hashtags : [],
        };
      });
      
      dispatch({ type: 'FETCH_TOP_TWEETS_SUCCESS', payload: tweets });
    } catch (error) {
      dispatch({ type: 'FETCH_TOP_TWEETS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ENGAGEMENT_START' });
    try {
      const response = await twitterAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchReachData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_REACH_START' });
    try {
      const response = await twitterAPI.getReachData(business._id);
      dispatch({ type: 'FETCH_REACH_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_REACH_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchHashtagsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_HASHTAGS_START' });
    try {
      const response = await twitterAPI.getHashtagsData(business._id);
      dispatch({ type: 'FETCH_HASHTAGS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchContentPerformanceData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_START' });
    try {
      const response = await twitterAPI.getContentPerformanceData(business._id);
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_SENTIMENT_START' });
    try {
      const response = await twitterAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchProblemsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_PROBLEMS_START' });
    try {
      const response = await twitterAPI.getProblemsData(business._id);
      dispatch({ type: 'FETCH_PROBLEMS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'Problem verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await twitterAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  return (
    <TwitterContext.Provider
      value={{
        state,
        fetchAccountData,
        fetchRecentTweets,
        fetchTopTweets,
        fetchEngagementData,
        fetchReachData,
        fetchHashtagsData,
        fetchContentPerformanceData,
        fetchSentimentData,
        fetchProblemsData,
        fetchAIAnalysis,
      }}
    >
      {children}
    </TwitterContext.Provider>
  );
};

export const useTwitter = (): TwitterContextType => {
  const context = useContext(TwitterContext);
  if (context === undefined) {
    throw new Error('useTwitter must be used within a TwitterProvider');
  }
  return context;
};
